import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography, Checkbox, Divider, Space } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { loginApi } from '../../services/api';
import { encryptMD5 } from '../../utils/crypto';
import './styles.css';

const { Title, Text } = Typography;

interface LoginFormValues {
  account: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  // Use the message hook for better context handling
  const [messageApi, contextHolder] = message.useMessage();

  // Check for saved credentials on component mount
  useEffect(() => {
    try {
      const savedAccount = localStorage.getItem('savedAccount');
      if (savedAccount) {
        form.setFieldsValue({ 
          account: savedAccount,
          remember: true
        });
      }
    } catch (error) {
      console.error('Error loading saved credentials:', error);
    }
  }, [form]);

  // Handle form input changes with debounce to reduce extension interference
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent event propagation to reduce extension interference
    e.stopPropagation();
  };

  const onFinish = async (values: LoginFormValues) => {
    try {
      setLoading(true);
      
      // Create a copy of values to avoid modifying the original object
      const loginData = { ...values };
      
      // Apply MD5 encryption to password
      loginData.password = encryptMD5(values.password);
      
      // Call login API with encrypted password
      const response = await loginApi(loginData.account, loginData.password);
      
      if (response.data.code === 0 && response.data.data) {
        // Store token and user info
        const { access_token, uid, realName } = response.data.data;
        
        try {
          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify({ uid, realName }));
          
          // Save account if remember is checked
          if (values.remember) {
            localStorage.setItem('savedAccount', values.account);
          } else {
            localStorage.removeItem('savedAccount');
          }
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
        
        messageApi.success('登录成功！');
        // Navigate to projects page
        navigate('/projects');
      } else {
        // Use messageApi instead of message directly
        messageApi.error(response.data.msg || '登录失败');
      }
    } catch (error) {
      console.error('登录错误:', error);
      messageApi.error('登录失败，请检查网络连接后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    // Prevent event propagation to reduce extension interference
    e.stopPropagation();
    messageApi.info('请联系系统管理员重置密码');
  };

  return (
    <div className="login-container">
      {/* Include the context holder to ensure messages are displayed */}
      {contextHolder}
      <Card className="login-card">
        <div className="login-header">
          <img src="/logo.svg" alt="Logo" style={{ height: 60, marginBottom: 16 }} />
          <Title level={4} style={{ color: '#666' }}>综合数据管理系统</Title>
          <Text style={{ color: '#999' }}>请输入您的账号和密码</Text>
        </div>
        
        <Form
          form={form}
          name="login"
          initialValues={{ remember: false }}
          onFinish={onFinish}
          size="large"
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="account"
            rules={[{ required: true, message: '请输入账号!' }]}
          >
            <Input 
              prefix={<UserOutlined className="site-form-item-icon" />} 
              placeholder="账号" 
              autoComplete="off"
              onChange={handleInputChange}
              onPaste={(e) => e.stopPropagation()}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码!' }]}
          >
            <Input.Password
              prefix={<LockOutlined className="site-form-item-icon" />}
              placeholder="密码"
              autoComplete="off"
              onChange={handleInputChange}
              onPaste={(e) => e.stopPropagation()}
            />
          </Form.Item>

          <Form.Item>
            <div className="login-form-options">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>记住账号</Checkbox>
              </Form.Item>
              <a className="login-form-forgot" onClick={handleForgotPassword}>
                忘记密码?
              </a>
            </div>
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              className="login-button" 
              loading={loading}
              block
              icon={<LoginOutlined />}
            >
              登录
            </Button>
          </Form.Item>
        </Form>
        
        <Divider plain>
          <Text type="secondary">智趣智慧工地</Text>
        </Divider>
        
        <div className="login-footer">
          <Text type="secondary">© 2025 数据管理系统</Text>
        </div>
      </Card>
    </div>
  );
};

export default Login; 